"use client";

import { useState, useCallback } from "react";
import { useAccount } from "wagmi";
import { formatEther } from "viem";
import { estimateLayerZeroFee } from "@/lib/layerzero-fee-estimation";

interface FeeEstimationState {
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  nativeFee: string | null;
  lzTokenFee: string | null;
  totalFee: string | null;
}

export function useLayerZeroFeeEstimation() {
  const { address } = useAccount();
  const [feeState, setFeeState] = useState<FeeEstimationState>({
    isLoading: false,
    isError: false,
    error: null,
    nativeFee: null,
    lzTokenFee: null,
    totalFee: null,
  });

  const estimateFee = useCallback(
    async (amount: string, tokenId: string) => {
      if (!amount || parseFloat(amount) <= 0 || !address) {
        setFeeState({
          isLoading: false,
          isError: false,
          error: null,
          nativeFee: null,
          lzTokenFee: null,
          totalFee: null,
        });
        return;
      }

      setFeeState(prev => ({ ...prev, isLoading: true, isError: false, error: null }));

      try {
        const result = await estimateLayerZeroFee({
          to: address,
          amount,
          tokenId,
          slippageBps: 50n, // 0.5%
          gasLimit: 3000000n, // 3,000,000
        });

        const { nativeFee, lzTokenFee } = result.quote;
        const totalFee = BigInt(nativeFee) + BigInt(lzTokenFee);

        setFeeState({
          isLoading: false,
          isError: false,
          error: null,
          nativeFee: formatEther(BigInt(nativeFee)),
          lzTokenFee: formatEther(BigInt(lzTokenFee)),
          totalFee: formatEther(totalFee),
        });
      } catch (error) {
        console.error("Fee estimation failed:", error);
        setFeeState({
          isLoading: false,
          isError: true,
          error: error instanceof Error ? error.message : "Failed to estimate fees",
          nativeFee: null,
          lzTokenFee: null,
          totalFee: null,
        });
      }
    },
    [address]
  );

  return {
    ...feeState,
    estimateFee,
  };
}